import type { ApolloError } from '@apollo/client';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import type { Dispatch, FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { BrandKey, CategoryFormat } from '../../__generated__/queries-web';
import { auth } from '../../utils/auth';
import { usePageCategories } from './apollo/queries/categories.web.graphql';
import {
  DEFAULT_ROOT_CATEGORY,
  FETCH_CATEGORIES_ERROR_MESSAGE,
} from './constants';
import type { Category } from './types';
import { getActivePathCategoryId, getBrandCategoriesWithPath } from './utils';

const INITIAL_ACTIVE_CATEGORY: Category | null = null;
const INITIAL_CATEGORIES: Category[] | null = [];

// CONTEXT
interface CategoriesContextProps {
  activeCategory: Category | null;
  activeCategoryRouteId: string | null;
  brandKey?: BrandKey;
  categories: Category[];
  categoriesError: ApolloError | null | undefined;
  isCategoriesLoading: boolean;
  refetchCategories: () => void;
  setActiveCategory: Dispatch<Category | null>;
  setCategories: Dispatch<Category[]>;
}
const CategoriesContext = createContext<CategoriesContextProps>({
  activeCategory: INITIAL_ACTIVE_CATEGORY,
  activeCategoryRouteId: null,
  categories: INITIAL_CATEGORIES,
  categoriesError: null,
  isCategoriesLoading: false,
  refetchCategories: () => null,
  setActiveCategory: () => null,
  setCategories: () => null,
});

// PROVIDER
interface CategoriesProviderProps {
  brandKey: BrandKey | undefined;
  children: ReactNode;
  format: CategoryFormat | undefined;
}
const CategoriesProvider: FC<CategoriesProviderProps> = ({
  brandKey,
  children,
  format,
}) => {
  const { pushNotification } = useNotification();
  const routeParams = useParams();
  const { error, data: dataCategories, loading, refetch } = usePageCategories({
    variables: {
      first: 1000,
      where: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        brandKey_in: [DEFAULT_ROOT_CATEGORY.brandKey, brandKey!],
        format,
        level_in: [1, 2, 3, 4, 5],
      },
    },
    skip: !brandKey,
  });

  const activeCategoryRouteId = useMemo(
    () => getActivePathCategoryId(routeParams),
    [routeParams],
  );

  const [activeCategory, setActiveCategory] = useState<Category | null>(
    INITIAL_ACTIVE_CATEGORY,
  );
  const [categories, setCategories] = useState<Category[]>(INITIAL_CATEGORIES);

  const refetchCategories = useCallback(() => {
    refetch().catch((e) => {
      if (e?.message) {
        pushNotification?.({
          type: NotificationTypeEnum.error,
          message: `
            [${FETCH_CATEGORIES_ERROR_MESSAGE}]
            ${e.message}
          `,
        });
      }
    });
  }, [pushNotification, refetch]);

  const refetchCategoryFormat = useCallback(() => {
    refetch({
      first: 1000,
      where: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        brandKey_in: [DEFAULT_ROOT_CATEGORY.brandKey, brandKey!],
        format: CategoryFormat.Category,
        level_in: [1, 2, 3, 4, 5],
      },
    }).catch((e) => {
      if (e?.message) {
        pushNotification?.({
          type: NotificationTypeEnum.error,
          message: `
            [${FETCH_CATEGORIES_ERROR_MESSAGE}]
            ${e.message}
          `,
        });
      }
    });
  }, [pushNotification, refetch, brandKey]);

  useEffect(() => {
    // Get all categories with their paths
    const categoriesWithPathname = getBrandCategoriesWithPath(
      dataCategories?.categories || [],
    );
    setCategories(categoriesWithPathname);
  }, [dataCategories]);

  useEffect(() => {
    if (error?.message) {
      pushNotification?.({
        type: NotificationTypeEnum.error,
        message: `
          [${FETCH_CATEGORIES_ERROR_MESSAGE}]
          ${error.message}
        `,
      });
    }
  }, [error, pushNotification]);

  /**
   * We setting the active category by listening when categories / routeParams changes
   **/
  useEffect(() => {
    if (!categories?.length) {
      setActiveCategory(null);
      return;
    }

    // If we have an activeCategoryRouteId, find the category with the matching ID
    if (activeCategoryRouteId) {
      const newActiveCategory =
        categories.find((category) => category.id === activeCategoryRouteId) ||
        null;

      if (newActiveCategory) {
        setActiveCategory(newActiveCategory as Category);
      } else {
        // If we still don't have the category, refetch with CategoryFormat.Category
        refetchCategoryFormat();
      }

      return;
    }

    // If no activeCategoryRouteId, set a default activeCategory
    let defaultCategory = null;

    // For superadmins, first try to find a level 1 category
    if (auth?.user?.isSuperAdmin()) {
      defaultCategory = categories.find(
        (category) => category.level === 1 && category.brandKey === brandKey,
      );
    }

    // If no level 1 category found or user is not superadmin, try level 2
    if (!defaultCategory) {
      defaultCategory = categories.find(
        (category) => category.level === 2 && category.brandKey === brandKey,
      );
    }

    // If still no category found, use the first category
    if (!defaultCategory && categories.length > 0) {
      const [firstCategory] = categories;
      defaultCategory = firstCategory;
    }

    setActiveCategory(defaultCategory as Category);
  }, [categories, activeCategoryRouteId, brandKey, refetchCategoryFormat]);

  return (
    <CategoriesContext.Provider
      value={{
        activeCategory,
        activeCategoryRouteId,
        brandKey,
        categories,
        categoriesError: error,
        isCategoriesLoading: loading,
        refetchCategories,
        setActiveCategory,
        setCategories,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export { CategoriesContext, CategoriesProvider };
